import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import {
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  LinkOff as RevokeIcon,
  FolderOpen as OpenFolderIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { downloadMenuLabelKey } from '../file-browser/bulkDownload';
import type { SharedEntry, SharedMenuModel } from './sharedViewModel';

interface SharedEntryMenuProps {
  anchorEl: HTMLElement | null;
  entry: SharedEntry | null;
  /**
   * What the menu offers for this right-click — `sharedMenuModel`, which
   * resolves the selection, direction and authority. `null` keeps it shut.
   */
  model: SharedMenuModel | null;
  /** True while this row's own download is in flight. Never the whole list. */
  downloadPending: boolean;
  /** True while this row's own revoke is in flight. Never the whole list. */
  revokePending: boolean;
  onClose: () => void;
  /**
   * Browse into a shared FOLDER (STD-029 B-4, D-6). Hands off to the Files
   * browser rather than rendering a second one here: R-12's "one Shared surface"
   * is about where grants are LISTED, and two browser components would be two
   * places to fix the next bug.
   */
  onOpen: (entry: SharedEntry) => void;
  /** Download every target: one file the usual way, several through the bulk run. */
  onDownload: (targets: SharedEntry[]) => void;
  onPlot: (entry: SharedEntry) => void;
  onRevoke: (entry: SharedEntry) => void;
}

/**
 * The actions on one shared entry.
 *
 * Shaped after `file-browser/dialogs/FileContextMenu` — same Menu, same icon
 * placement, same "hide what the API would refuse rather than offer and explain"
 * rule — but deliberately not the same component. That one always offers Delete
 * and Rename, which act on rows this organization owns; nothing on this screen
 * does. Reusing it would mean adding two "is this actually ours?" flags to the
 * browser's menu, which is the shape #1242 exists to remove. What the two DO
 * share since #1692 is the part that is the same act on both screens: the
 * Download item's label (`downloadMenuLabelKey`) and the bulk run behind
 * "Download (N files)" (`file-browser/bulkDownload.ts`).
 *
 * Every item's enabled state is scoped to THIS row. A single mutation instance
 * serves the whole grid, so `isPending` read straight off it disables every row
 * at once — #1205 for Download, #1232 for Revoke. See `pendingTargetId`.
 */
const SharedEntryMenu: React.FC<SharedEntryMenuProps> = ({
  anchorEl,
  entry,
  model,
  downloadPending,
  revokePending,
  onClose,
  onOpen,
  onDownload,
  onPlot,
  onRevoke,
}) => {
  const { t } = useTranslation();
  const downloadCount = model?.downloadTargets.length ?? 0;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl && entry && model && !model.isEmpty)}
      onClose={onClose}
    >
      {entry && model?.canOpen && (
        <MenuItem onClick={() => onOpen(entry)} data-testid="shared-entry-open">
          <OpenFolderIcon sx={{ mr: 1 }} />
          {t('sharedView.openFolder')}
        </MenuItem>
      )}
      {/* The label counts the TARGETS (`downloadMenuLabelKey`, shared with the
          Files browser): with five selected, "Download" fetched one. */}
      {model && downloadCount > 0 && (
        <MenuItem
          disabled={downloadPending}
          onClick={() => onDownload(model.downloadTargets)}
          data-testid="shared-entry-download"
        >
          <DownloadIcon sx={{ mr: 1 }} />
          {t(downloadMenuLabelKey(downloadCount), { count: downloadCount })}
        </MenuItem>
      )}
      {entry && model?.canPlot && (
        <MenuItem onClick={() => onPlot(entry)} data-testid="shared-entry-plot">
          <TimelineIcon sx={{ mr: 1 }} />
          {t('sharedView.plot')}
        </MenuItem>
      )}
      {entry && model?.canRevoke && (
        <MenuItem
          disabled={revokePending}
          onClick={() => onRevoke(entry)}
          sx={{ color: 'error.main' }}
          data-testid="shared-entry-revoke"
        >
          <RevokeIcon sx={{ mr: 1 }} />
          {t('sharedView.revoke')}
        </MenuItem>
      )}
    </Menu>
  );
};

export default SharedEntryMenu;
